import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useNavigate, useParams } from 'react-router-dom';
import {
  decideReferralPortfolioReview,
  getReferrerPortfolioReviews,
  submitReferralPortfolioReview,
} from '../../../services/api';
import {
  BRAND_GOLD,
  FONT_BODY,
  FONT_HEAVY,
  currentUserEmail,
  loadManagedPortfolios,
  money,
} from './managedPortfoliosData';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: '110px 24px 70px',
    fontFamily: FONT_BODY,
  },
  shell: { maxWidth: 1040, margin: '0 auto' },
  back: {
    color: `${theme.palette.text.secondary} !important`,
    textTransform: 'none !important',
    paddingLeft: '0 !important',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    margin: '20px 0 26px',
    flexWrap: 'wrap',
  },
  ident: { display: 'flex', alignItems: 'center', gap: 14 },
  avatar: {
    background: `${theme.palette.primary.main} !important`,
    color: '#fff !important',
    fontWeight: '700 !important',
    width: '52px !important',
    height: '52px !important',
  },
  name: { fontFamily: FONT_HEAVY, fontSize: 24, lineHeight: 1.2 },
  muted: { color: theme.palette.text.secondary, fontSize: 13 },
  primaryBtn: {
    background: `${BRAND_GOLD} !important`,
    color: '#25362f !important',
    fontWeight: '700 !important',
    textTransform: 'none !important',
  },
  panel: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: 22,
    marginBottom: 18,
  },
  sectionTitle: {
    fontFamily: FONT_HEAVY,
    fontSize: 15,
    fontWeight: 700,
    margin: '0 0 14px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' },
  },
  info: { background: theme.palette.action.hover, borderRadius: 10, padding: 12 },
  infoLabel: { color: theme.palette.text.secondary, fontSize: 12 },
  infoValue: { fontWeight: 700, marginTop: 4 },
  allocationRow: { marginBottom: 14 },
  allocationTop: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    marginBottom: 6,
  },
  progress: {
    height: '7px !important',
    borderRadius: '8px !important',
    background: `${theme.palette.action.hover} !important`,
    '& .MuiLinearProgress-bar': { background: theme.palette.primary.main },
  },
  note: {
    padding: '13px 15px',
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    borderRadius: 10,
    fontSize: 13,
    marginTop: 6,
  },
  review: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '16px 0',
  },
  reviewHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 6,
  },
  allocEditorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  state: { padding: 60, textAlign: 'center', color: theme.palette.text.secondary },
}));

const REVIEW_STATUS_LABEL = {
  pending_client_approval: 'Awaiting client approval',
  approved: 'Approved',
  rejected: 'Rejected',
};
const reviewChipColor = (status) =>
  status === 'approved'
    ? 'success'
    : status === 'rejected'
    ? 'error'
    : 'warning';

const allocationSummary = (allocation) => {
  if (!allocation || typeof allocation !== 'object') return null;
  const entries = Object.entries(allocation);
  if (entries.length === 0) return null;
  return entries.map(([asset, pct]) => `${asset} ${pct}%`).join(' · ');
};

export default function ManagedPortfolioDetail() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { portfolioId: rawId } = useParams();
  const portfolioId = decodeURIComponent(rawId || '');

  const [portfolio, setPortfolio] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [note, setNote] = useState('Quarterly portfolio review.');
  const [allocDraft, setAllocDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [decidingId, setDecidingId] = useState('');

  const me = useMemo(() => currentUserEmail().toLowerCase(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await loadManagedPortfolios();
      const match = rows.find((row) => String(row.portfolioId) === portfolioId);
      setPortfolio(match || null);
      if (match) {
        try {
          const res = await getReferrerPortfolioReviews({ portfolioId });
          setReviews((res && res.data && res.data.reviews) || []);
        } catch (e) {
          setReviews([]);
        }
      }
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          'Unable to load this portfolio. Please sign in again or try later.'
      );
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    load();
  }, [load]);

  const openDialog = () => {
    const current = (portfolio?.allocation || []).map(([asset, pct]) => ({
      asset,
      pct: Number(pct) || 0,
    }));
    setAllocDraft(current);
    setNote('Quarterly portfolio review.');
    setDialogOpen(true);
  };

  const draftTotal = allocDraft.reduce(
    (sum, item) => sum + (Number(item.pct) || 0),
    0
  );
  const allocationValid =
    allocDraft.length === 0 || Math.abs(draftTotal - 100) <= 0.5;

  const saveReview = async () => {
    if (!portfolio || !note.trim() || !allocationValid) return;
    setSaving(true);
    setError('');
    try {
      const proposedAllocation = allocDraft
        .filter((item) => item.asset && item.asset.trim())
        .reduce((acc, item) => {
          acc[item.asset.trim()] = Number(item.pct) || 0;
          return acc;
        }, {});
      await submitReferralPortfolioReview({
        clientEmail: portfolio.clientEmail,
        portfolioId: portfolio.portfolioId,
        portfolioType: portfolio.product,
        note: note.trim(),
        proposedAllocation:
          Object.keys(proposedAllocation).length > 0
            ? proposedAllocation
            : undefined,
      });
      setDialogOpen(false);
      await load();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          'The review could not be submitted.'
      );
    } finally {
      setSaving(false);
    }
  };

  const decide = async (reviewId, decision) => {
    setDecidingId(reviewId);
    try {
      await decideReferralPortfolioReview(reviewId, decision);
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error || 'Could not record the client decision.'
      );
    } finally {
      setDecidingId('');
    }
  };

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.shell}>
          <Box className={classes.state}>
            <CircularProgress size={28} />
            <p>Loading portfolio…</p>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!portfolio) {
    return (
      <Box className={classes.page}>
        <Box className={classes.shell}>
          <Button
            className={classes.back}
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/referral/managed-portfolios')}
          >
            Back to Managed Portfolios
          </Button>
          <Box className={classes.state}>
            <h3>Portfolio not found</h3>
            <p>
              {error ||
                'This portfolio is no longer available or you do not have access to it.'}
            </p>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.shell}>
        <Button
          className={classes.back}
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/referral/managed-portfolios')}
        >
          Back to Managed Portfolios
        </Button>

        <Box className={classes.header}>
          <Box className={classes.ident}>
            <Avatar className={classes.avatar}>{portfolio.initials}</Avatar>
            <Box>
              <div className={classes.name}>{portfolio.clientName}</div>
              <div className={classes.muted}>
                {portfolio.product} · {portfolio.plan}
              </div>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Chip
              size="small"
              color={portfolio.status === 'On track' ? 'success' : 'warning'}
              label={portfolio.status}
            />
            <Button
              className={classes.primaryBtn}
              variant="contained"
              startIcon={<EditRoundedIcon />}
              onClick={openDialog}
            >
              Review portfolio
            </Button>
          </Box>
        </Box>

        {error && <Box className={classes.note}>{error}</Box>}

        <Box className={classes.panel}>
          <div className={classes.sectionTitle}>Overview</div>
          <Box className={classes.infoGrid}>
            <Box className={classes.info}>
              <div className={classes.infoLabel}>Portfolio value</div>
              <div className={classes.infoValue}>{money(portfolio.value)}</div>
            </Box>
            <Box className={classes.info}>
              <div className={classes.infoLabel}>Order status</div>
              <div className={classes.infoValue}>
                {portfolio.orderStatus || portfolio.status}
              </div>
            </Box>
            <Box className={classes.info}>
              <div className={classes.infoLabel}>Risk mandate</div>
              <div className={classes.infoValue}>{portfolio.risk || 'N/A'}</div>
            </Box>
            <Box className={classes.info}>
              <div className={classes.infoLabel}>Opened</div>
              <div className={classes.infoValue}>
                {portfolio.createdAt
                  ? new Date(portfolio.createdAt).toLocaleDateString()
                  : 'N/A'}
              </div>
            </Box>
          </Box>
        </Box>

        <Box className={classes.panel}>
          <div className={classes.sectionTitle}>Current allocation</div>
          {portfolio.allocation.length > 0 ? (
            portfolio.allocation.map(([asset, percent]) => (
              <Box className={classes.allocationRow} key={asset}>
                <Box className={classes.allocationTop}>
                  <span>{asset}</span>
                  <b>{percent}%</b>
                </Box>
                <LinearProgress
                  className={classes.progress}
                  variant="determinate"
                  value={Math.max(0, Math.min(100, Number(percent) || 0))}
                />
              </Box>
            ))
          ) : (
            <p className={classes.muted}>
              Allocation details are not stored on this portfolio order. You can
              still propose a target allocation in a review.
            </p>
          )}
          <Box className={classes.note}>
            Client approval is required before any proposed portfolio change can
            proceed. A review never places a trade on its own.
          </Box>
        </Box>

        <Box className={classes.panel}>
          <div className={classes.sectionTitle}>
            Reviews &amp; proposals ({reviews.length})
          </div>
          {reviews.length === 0 && (
            <p className={classes.muted}>No reviews recorded yet.</p>
          )}
          {reviews.map((review) => {
            const summary = allocationSummary(review.proposedAllocation);
            const canDecide =
              me &&
              String(review.clientEmail).toLowerCase() === me &&
              review.status === 'pending_client_approval';
            return (
              <Box className={classes.review} key={review._id}>
                <Box className={classes.reviewHead}>
                  <Chip
                    size="small"
                    color={reviewChipColor(review.status)}
                    label={
                      REVIEW_STATUS_LABEL[review.status] || review.status
                    }
                  />
                  <span className={classes.muted}>
                    {review.createdAt
                      ? new Date(review.createdAt).toLocaleString()
                      : ''}
                  </span>
                </Box>
                <div>{review.note}</div>
                {summary && (
                  <div className={classes.muted} style={{ marginTop: 6 }}>
                    Proposed: {summary}
                  </div>
                )}
                {canDecide && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={decidingId === review._id}
                      onClick={() => decide(review._id, 'approved')}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={decidingId === review._id}
                      onClick={() => decide(review._id, 'rejected')}
                    >
                      Reject
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Review {portfolio.clientName}’s portfolio</DialogTitle>
        <DialogContent>
          <p className={classes.muted}>
            Record your recommendation. It is sent to the client for approval and
            does not place a trade.
          </p>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Advisor note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />

          <div className={classes.sectionTitle}>
            Proposed allocation (optional)
          </div>
          {allocDraft.map((item, index) => (
            <Box className={classes.allocEditorRow} key={index}>
              <TextField
                size="small"
                label="Asset"
                value={item.asset}
                onChange={(e) => {
                  const next = [...allocDraft];
                  next[index] = { ...next[index], asset: e.target.value };
                  setAllocDraft(next);
                }}
              />
              <TextField
                size="small"
                type="number"
                label="%"
                value={item.pct}
                onChange={(e) => {
                  const next = [...allocDraft];
                  next[index] = { ...next[index], pct: e.target.value };
                  setAllocDraft(next);
                }}
                sx={{ width: 90 }}
              />
              <IconButton
                size="small"
                onClick={() =>
                  setAllocDraft(allocDraft.filter((_, i) => i !== index))
                }
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() =>
              setAllocDraft([...allocDraft, { asset: '', pct: 0 }])
            }
          >
            Add asset
          </Button>
          {allocDraft.length > 0 && (
            <div
              className={classes.muted}
              style={{
                marginTop: 8,
                color: allocationValid ? undefined : '#d33',
              }}
            >
              Total: {draftTotal}% {allocationValid ? '' : '(must total 100%)'}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            className={classes.primaryBtn}
            variant="contained"
            onClick={saveReview}
            disabled={saving || !note.trim() || !allocationValid}
          >
            {saving ? 'Submitting…' : 'Send for client approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
